import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { Gem } from 'lucide-react';

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 20px;
`;

const ModalCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  animation: shake 0.5s ease;
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
    75% { transform: translateX(10px); }
  }
`;

const Title = styled.h3`
  margin: 0 0 12px 0;
  font-size: 20px;
  color: #dc3545;
`;

const Message = styled.p`
  color: #6c757d;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const CurrentBalance = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 12px;
  margin-bottom: 24px;
  
  .icon {
    font-size: 24px;
  }
  
  .text {
    font-size: 16px;
    color: #856404;
    
    strong {
      font-weight: 700;
      color: #212529;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

interface InsufficientCreditsModalProps {
  isOpen: boolean;
  currentCredits: number;
  onPurchase: () => void;
  onClose: () => void;
}

export const InsufficientCreditsModal: React.FC<InsufficientCreditsModalProps> = ({
  isOpen,
  currentCredits,
  onPurchase,
  onClose
}) => {
  const { t } = useTranslation();
  
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <Gem/>
        <Title>{t('credits.insufficientTitle', 'Not Enough Credits')}</Title>
        <Message>
          {t('credits.insufficientMessage', 'You need at least 1 credit to generate a story. Get more credits to continue creating magical bedtime stories!')}
        </Message>
        
        <CurrentBalance>
          <span className="icon">💰</span>
          <span className="text">
            {t('credits.yourBalance', 'Your balance:')} <strong>{currentCredits}</strong>
          </span>
        </CurrentBalance>
        
        <Actions>
          <Button
            variant="outline"
            size="medium"
            onClick={onClose}
          >
            {t('common.close', 'Close')}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={onPurchase}
          >
            {t('credits.getCredits', 'Get Credits')} 
            <Gem/>
          </Button>
        </Actions>
      </ModalCard>
    </ModalOverlay>
  );
};